import { useState } from "react";
import { motion } from "framer-motion";
import {
  ShoppingBag,
  IndianRupee,
  Package,
  CreditCard,
  XCircle,
  RotateCcw,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle2,
} from "lucide-react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { toast } from "sonner";
import { MOCK_DASHBOARD, COMING_SOON_FOR_NON_ADMIN } from "../../../lib/panel-data";
import { useAuth } from "../../../lib/auth-context";
import { ComingSoon } from "../ComingSoon";

const METRIC_ICONS = {
  orders: ShoppingBag,
  revenue: IndianRupee,
  cod: Package,
  prepaid: CreditCard,
  cancelled: XCircle,
  returns: RotateCcw,
};

const METRIC_TINTS = {
  orders: "bg-purple-50 text-purple-700",
  revenue: "bg-emerald-50 text-emerald-700",
  cod: "bg-amber-50 text-amber-700",
  prepaid: "bg-violet-50 text-violet-700",
  cancelled: "bg-red-50 text-red-600",
  returns: "bg-slate-100 text-slate-700",
};

export const DashboardPage = () => {
  const { user } = useAuth();
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState("Just now");

  if (COMING_SOON_FOR_NON_ADMIN && user?.userType !== 1) {
    return <ComingSoon title="Dashboard" description="Live sales dashboards are rolling out to all accounts soon." />;
  }

  const doSync = () => {
    setSyncing(true);
    setTimeout(() => {
      setSyncing(false);
      setLastSync("Just now");
      toast.success("Synced", { description: "Latest orders pulled from your seller panel." });
    }, 900);
  };

  return (
    <div className="space-y-6" data-testid="panel-dashboard">
      {/* Top sync card */}
      <div className="rounded-3xl border border-purple-100 bg-white p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="font-heading text-lg font-extrabold text-slate-900">Daily Report</div>
          <div className="text-xs text-slate-500 mt-1">
            {MOCK_DASHBOARD.date} · Synced {lastSync}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2 text-xs text-slate-500">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            Manual sync · safe & private
          </div>
          <Button
            onClick={doSync}
            disabled={syncing}
            data-testid="panel-dashboard-sync"
            className="rounded-full bg-purple-600 hover:bg-purple-700 text-white font-semibold shadow-lg shadow-purple-600/25"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Sync now"}
          </Button>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        {MOCK_DASHBOARD.metrics.map((m, i) => {
          const Icon = METRIC_ICONS[m.id] || ShoppingBag;
          const tint = METRIC_TINTS[m.id] || "bg-purple-50 text-purple-700";
          const positive = m.delta?.startsWith("+");
          return (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              data-testid={`dashboard-metric-${m.id}`}
              className="relative rounded-2xl border border-slate-200 bg-white p-4 hover:shadow-md transition-shadow"
            >
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${tint}`}>
                <Icon className="w-4.5 h-4.5" strokeWidth={2.2} />
              </div>
              <div className="text-[11px] text-slate-500 font-medium">{m.label}</div>
              <div className="font-heading text-2xl font-extrabold text-slate-900 mt-1 tabular-nums">
                {m.prefix || ""}
                {m.value.toLocaleString("en-IN")}
              </div>
              {m.delta && (
                <div
                  className={`absolute top-3 right-3 inline-flex items-center gap-0.5 text-[10px] font-bold ${
                    positive ? "text-emerald-600" : "text-red-500"
                  }`}
                >
                  {positive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                  {m.delta}
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Splits + Activity */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="rounded-3xl border border-purple-100 bg-white p-6">
          <div className="font-heading text-base font-extrabold text-slate-900">Platform Split</div>
          <div className="text-xs text-slate-500 mt-1">Today&rsquo;s orders by platform</div>
          <div className="mt-5 space-y-4">
            {MOCK_DASHBOARD.splits.map((s) => (
              <div key={s.platform}>
                <div className="flex justify-between text-xs mb-1.5">
                  <span className="font-bold text-slate-700">{s.platform}</span>
                  <span className="text-slate-500 tabular-nums">{s.orders} orders</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${s.pct}%` }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    className={`h-full rounded-full ${s.color}`}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-purple-100 bg-purple-50/30 p-3">
              <div className="text-[10px] uppercase font-bold tracking-wider text-purple-700">Best Hour</div>
              <div className="font-heading text-base font-bold text-slate-900 mt-1">7–8 PM</div>
            </div>
            <div className="rounded-xl border border-emerald-100 bg-emerald-50/30 p-3">
              <div className="text-[10px] uppercase font-bold tracking-wider text-emerald-700">Avg Order</div>
              <div className="font-heading text-base font-bold text-slate-900 mt-1">₹813</div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 rounded-3xl border border-purple-100 bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-heading text-base font-extrabold text-slate-900">Today&rsquo;s Activity</div>
              <div className="text-xs text-slate-500 mt-1">Latest events from your stores</div>
            </div>
            <Badge className="rounded-full bg-purple-100 text-purple-700 hover:bg-purple-100">Live</Badge>
          </div>
          <ul className="divide-y divide-slate-100">
            {MOCK_DASHBOARD.activity.map((row) => (
              <li key={row.id} className="flex items-center justify-between py-2.5">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">{row.label}</div>
                  <div className="text-[11px] text-slate-500">{row.when}</div>
                </div>
                <div className="text-sm font-bold text-purple-700 whitespace-nowrap">{row.amount}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
